
import React, { useState } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Filter, UserPlus, Loader2 } from 'lucide-react';
import PatientFilterCard from '@/components/doctor/PatientFilterCard';
import PatientEmptyState from '@/components/doctor/PatientEmptyState';
import {
  ClipboardList,
  Calendar,
  FileText,
  Stethoscope,
  File,
  ArrowRight
} from 'lucide-react';
import { usePatientData } from '@/hooks/usePatientData';

const MyPatientsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const { patients, isLoading, error } = usePatientData();
  
  const filteredPatients = patients.filter(patient => 
    patient.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    patient.email?.toLowerCase().includes(searchQuery.toLowerCase() || '')
  );

  // Format date to a more readable format
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Not Scheduled';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="container mx-auto p-2">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">My Patients</h1>
          <p className="text-muted-foreground">Manage and access your patient records</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/create-patient')} className="flex gap-1 items-center">
            <UserPlus className="h-4 w-4" />
            <span>Add Patient</span>
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        <div className={`${showFilters ? 'lg:order-2 lg:w-3/4' : 'w-full'}`}>
          <Card className="overflow-hidden mb-6">
            <CardHeader className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center pb-2">
              <CardTitle>Patient List</CardTitle>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <div className="relative w-full sm:w-auto">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input 
                    type="search" 
                    placeholder="Search patients..." 
                    className="pl-8 w-full sm:w-[300px]" 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={() => setShowFilters(!showFilters)}
                  className={showFilters ? 'bg-muted' : ''}
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mr-2" />
                  <span>Loading patients...</span>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <p className="text-red-500 mb-2">Error loading patients</p>
                  <p>{error}</p>
                </div>
              ) : filteredPatients.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Patient</TableHead>
                        <TableHead className="hidden lg:table-cell">Status</TableHead>
                        <TableHead className="hidden lg:table-cell">Last Visit</TableHead>
                        <TableHead className="hidden md:table-cell">Next Appointment</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPatients.map((patient) => (
                        <TableRow key={patient.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
                                {patient.avatarUrl && (
                                  <img 
                                    src={patient.avatarUrl} 
                                    alt={patient.name} 
                                    className="w-full h-full object-cover"
                                  />
                                )}
                              </div>
                              <div>
                                <p className="font-medium line-clamp-1">{patient.name}</p>
                                <p className="text-xs text-muted-foreground md:hidden">{patient.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              patient.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {patient.status}
                            </span>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">{formatDate(patient.lastAppointment)}</TableCell>
                          <TableCell className="hidden md:table-cell">
                            {formatDate(patient.nextAppointment)}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              <Button 
                                variant="outline" 
                                size="icon" 
                                asChild
                                className="h-8 w-8"
                                title="Treatment Plans"
                              >
                                <Link to={`/treatment-plans/${patient.id}`}>
                                  <ClipboardList className="h-4 w-4" />
                                </Link>
                              </Button>
                              <Button 
                                variant="outline" 
                                size="icon" 
                                asChild
                                className="h-8 w-8"
                                title="Treatment Sessions"
                              >
                                <Link to={`/treatment-sessions/${patient.id}`}>
                                  <Calendar className="h-4 w-4" />
                                </Link>
                              </Button>
                              <Button 
                                variant="outline" 
                                size="icon" 
                                asChild
                                className="h-8 w-8"
                                title="Follow-up Reports"
                              >
                                <Link to={`/follow-up-reports/${patient.id}`}>
                                  <FileText className="h-4 w-4" />
                                </Link>
                              </Button>
                              <Button 
                                variant="outline" 
                                size="icon" 
                                asChild
                                className="h-8 w-8"
                                title="Post-Treatment Reports"
                              >
                                <Link to={`/post-treatment/${patient.id}`}>
                                  <Stethoscope className="h-4 w-4" />
                                </Link>
                              </Button>
                              <Button 
                                variant="outline" 
                                size="icon" 
                                asChild
                                className="h-8 w-8"
                                title="Medical Documents"
                              >
                                <Link to={`/medical-documents/${patient.id}`}>
                                  <File className="h-4 w-4" />
                                </Link>
                              </Button>
                              <Button 
                                variant="default" 
                                size="icon"
                                asChild
                                className="h-8 w-8"
                                title="View Case File"
                              >
                                <Link to={`/case-file/${patient.id}`}>
                                  <ArrowRight className="h-4 w-4" />
                                </Link>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <PatientEmptyState onAddPatient={() => navigate('/create-patient')} />
              )}
            </CardContent>
          </Card>
        </div>
        
        {showFilters && (
          <div className="lg:order-1 lg:w-1/4">
            <PatientFilterCard onFilter={() => {}} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MyPatientsPage;
